import * as React from 'react';
import { withTheme } from 'emotion-theming';
import { Box } from '../box';
import { ZStack } from '../stack';
import { CircularProgress } from '../progress';
import { Text } from '../text';
import { MotionValue } from '../motion';
// import { withTheme } from 'styled-components'
import { bezier } from '../../utils/bezier';
import { responsiveValue } from '../../utils/layout';

type Props = {
    size?: 'sm' | 'md' | 'lg';
    label?: string;
    theme?: any;
    value?: number;
    labelValue?: number;
    range: { min: number; max: number };
    units?: string;
    color?: string;
    bgColor?: string;
    disabledBgColor?: string;
    formatter?: (value: number) => string;
    converter?: (progress: number) => number;
    animation?: any;
    disabled?: boolean;
};

const configForSize = (size: string): any => {
    switch (size) {
    case 'sm':
        return {
            progress: {
                height: 100,
                width: 100,
                strokeWidth: 12
            },
            value: {
                bottom: 35,
                fontSize: '24px',
                lineHeight: '24px'
            },
            units: {
                bottom: 24,
                fontSize: '11px'
            },
            label: {
                fontSize: 'xs'
            }
        };
    case 'md':
        return {
            progress: {
                height: 140,
                width: 140,
                strokeWidth: 14
            },
            value: {
                bottom: 47,
                fontSize: '34px',
                lineHeight: '34px'
            },
            units: {
                bottom: 32,
                fontSize: '12px'
            },
            label: {
                fontSize: 'sm'
            }
        };
    case 'lg':
        return {
            progress: {
                height: 160,
                width: 160,
                strokeWidth: 16
            },
            value: {
                bottom: 55,
                fontSize: '40px',
                lineHeight: '40px'
            },
            units: {
                bottom: 35,
                fontSize: '14px'
            },
            label: {
                fontSize: 'sm'
            }
        };
    default:
    }

    return null;
};

const CircularMeter: React.FC<Props> = (props: Props): React.ReactElement => {
    const {
        size,
        label,
        value: initialValue,
        labelValue,
        range,
        units,
        color,
        bgColor = 'bg.100',
        disabledBgColor = 'brand.gray.100',
        formatter,
        converter,
        animation,
        disabled,
        ...rest
    } = props;

    const hasLabelValue = labelValue !== null && typeof labelValue !== 'undefined' && Number.isNaN(labelValue) === false;

    const opts = size ? configForSize(size) : {};
    const val = undefined !== initialValue && !Number.isNaN(initialValue) ? initialValue : range.min;
    const clampedValue = Math.min(range.max, Math.max(range.min, val));
    const delay = animation ? animation.delay : 0;
    let progress = disabled ? 0 : (clampedValue - range.min) / (range.max - range.min);

    if (converter) {
        progress = converter(progress);
    }

    const [value, setValue] = React.useState(0);
    const [lastValue, setLastValue] = React.useState(0);

    React.useEffect(() => {
        setValue((prev) => {
            setLastValue(prev);
            return clampedValue;
        });
    }, [initialValue]);

    return (
        <Box {...rest}>
            <ZStack height={opts.progress.height} width={opts.progress.width} margin="0 auto">
                <CircularProgress
                    theme={props.theme}
                    anchor="center-x"
                    bgColor={disabled ? disabledBgColor : bgColor}
                    value={progress}
                    valueColor={disabled ? 'brand.gray.100' : color}
                    start="left"
                    startAngle={-50}
                    endAngle={230}
                    strokeWidth={opts.progress.strokeWidth}
                    width={opts.progress.width}
                    height={opts.progress.height}
                    animation={hasLabelValue ? false : animation}
                />
                {disabled ? (
                    <Text
                        variant="value"
                        m={0}
                        fontSize={opts.value.fontSize}
                        lineHeight={opts.value.lineHeight}
                        color={disabled ? 'text.base.tertiary' : 'text.base.primary'}
                        anchor="center-x"
                        bottom={opts.value.bottom}
                    >
                        --
                    </Text>
                ) : (
                    <MotionValue
                        variant="value"
                        m={0}
                        fromValue={lastValue}
                        toValue={hasLabelValue ? labelValue : value}
                        duration={1}
                        easing={hasLabelValue ? 'none' : 'easeOutQuad'}
                        formatter={formatter}
                        delay={delay}
                        fontSize={opts.value.fontSize}
                        lineHeight={opts.value.lineHeight}
                        color={disabled ? 'text.base.tertiary' : 'text.base.primary'}
                        anchor="center-x"
                        bottom={opts.value.bottom}
                    />
                )}
                <Text
                    m={0}
                    fontSize={opts.units.fontSize}
                    color="text.base.secondary"
                    anchor="center-x"
                    bottom={opts.units.bottom}
                >
                    {units}
                </Text>
            </ZStack>
            <Text
                mt={-2}
                variant="label"
                fontSize={opts.label.fontSize}
                color={disabled ? 'text.base.tertiary' : 'text.base.primary'}
                textAlign="center"
            >
                {label}
            </Text>
        </Box>
    );
};

CircularMeter.defaultProps = {
    size: 'md',
    value: 0,
    range: {
        min: 0,
        max: 1
    },
    disabled: false
};

const ThemeWrapper = withTheme(CircularMeter);

export { ThemeWrapper as CircularMeter };
